import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import classNames from "classnames";
import dayjs from 'dayjs';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import AvatarList from '@components/avatarList';
import { BASE_IMG_URL } from '@images/imageUrls';
import { formatDateTime } from '@utils/timeUtils';
import { px2rem } from '@utils/px2rem';

interface ProductProgressBarProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    data?: {
        status?: number;  // 0:未开始, 1:报名中, 2:第一轮公布后, 3:第二轮公布后, 4:已完成
        recruit?: number;
        enrolls?: number;
        enroll_start_at?: string;
        first_round_at?: string;
        second_round_at?: string;
        first_people?: any
        second_people?: any
        latest_enroll_users?: any
    };
}

type ProductProgressBarAllProps = ProductProgressBarProps & RouteComponentProps;

// 进度条状态枚举
enum ProgressStatus {
    NOT_STARTED = 0,    // 报名未开始
    ENROLLING = 1,      // 报名进行中
    AFTER_FIRST = 2,    // 第一轮公布后（报名截止到第二轮公布前）
    AFTER_SECOND = 3,   // 第二轮公布后
    FINISHED = 4        // 已完成
}

@inject('app', 'site', 'user')
@observer
class ProductProgressBar extends React.Component<ProductProgressBarAllProps> {
    constructor(props: ProductProgressBarAllProps) {
        super(props);
    }

    componentDidMount() { }

    // 获取当前进度状态（优先使用传入的status）
    getProgressStatus = (): number => {
        const { data } = this.props;
        
        // 如果传入了status，直接使用
        if (data?.status !== undefined) {
            return data.status;
        }
        
        // 否则根据时间计算
        const { enroll_start_at, first_round_at, second_round_at } = data || {};
        
        const nowTime = dayjs();
        const startTime = dayjs(enroll_start_at);
        const firstTime = dayjs(first_round_at);
        const secondTime = dayjs(second_round_at);

        // 报名未开始
        if (nowTime.isBefore(startTime)) {
            return ProgressStatus.NOT_STARTED;
        }
        // 报名进行中 (从报名开始到第一轮公布)
        else if (nowTime.isBefore(firstTime)) {
            return ProgressStatus.ENROLLING;
        }
        // 报名截止到第二轮公布前 (第一轮公布后到第二轮公布)
        else if (nowTime.isBefore(secondTime)) {
            return ProgressStatus.AFTER_FIRST;
        }
        // 第二轮公布后
        else {
            return ProgressStatus.AFTER_SECOND;
        }
    }

    // 获取小V位置 (thumbPosition)
    getThumbPosition = (status: number): number => {
        switch(status) {
            case ProgressStatus.NOT_STARTED:  // status 0
                return 0;
            case ProgressStatus.ENROLLING:     // status 1
                return 17;
            case ProgressStatus.AFTER_FIRST:   // status 2
                return 50;
            case ProgressStatus.AFTER_SECOND:  // status 3
                return 50;
            case ProgressStatus.FINISHED:      // status 4
                return 100;
            default:
                return 0;
        }
    }

    // 获取进度条填充宽度 (progress)
    getProgressWidth = (status: number): number => {
        switch(status) {
            case ProgressStatus.NOT_STARTED:   // status 0
                return 0;
            case ProgressStatus.ENROLLING:      // status 1
                return 33;
            case ProgressStatus.AFTER_FIRST:    // status 2
                return 66;
            case ProgressStatus.AFTER_SECOND:   // status 3
                return 66;
            case ProgressStatus.FINISHED:       // status 4
                return 100;
            default:
                return 0;
        }
    }

    // 获取状态文本
    getStatusText = (status: number): string => {
        switch(status) {
            case ProgressStatus.NOT_STARTED:
                return '报名即将开始';
            case ProgressStatus.ENROLLING:
                return '报名进行中';
            case ProgressStatus.AFTER_FIRST:
                return '第一轮已公布';
            case ProgressStatus.AFTER_SECOND:
                return '第二轮已公布';
            case ProgressStatus.FINISHED:
                return '活动已结束';
            default:
                return '';
        }
    }

    // 判断时间项是否激活
    isTimeItemActive = (status: number, itemIndex: number): boolean => {
        // itemIndex: 0-报名时间, 1-第一轮名单公布, 2-第二轮名单公布
        switch(status) {
            case ProgressStatus.NOT_STARTED:
                return false;
            case ProgressStatus.ENROLLING:
                return itemIndex === 0;
            case ProgressStatus.AFTER_FIRST:
                return itemIndex === 0 || itemIndex === 1;
            case ProgressStatus.AFTER_SECOND:
            case ProgressStatus.FINISHED:
                return true;
            default:
                return false;
        }
    }

    // 判断小V图片使用哪个（完成状态用per2）
    isFinishedStatus = (status: number): boolean => {
        return status === ProgressStatus.AFTER_SECOND || status === ProgressStatus.FINISHED;
    }

    // 根据thumbPosition获取marginLeft偏移量
    getMarginLeft = (thumbPosition: number): string => {
        if (thumbPosition === 0) {
            return px2rem(0);
        }
        if (thumbPosition === 100) {
            return px2rem(-64);
        }
        return px2rem(-32);
    }

    render() {
        const { data } = this.props;
        const { enroll_start_at, first_round_at, second_round_at } = data || {};

        const startTime = formatDateTime(enroll_start_at);
        const moveTime = formatDateTime(first_round_at);
        const endTime = formatDateTime(second_round_at);
        
        const progressStatus = this.getProgressStatus();
        const thumbPosition = this.getThumbPosition(progressStatus);
        const progressWidth = this.getProgressWidth(progressStatus);
        const statusText = this.getStatusText(progressStatus);
        
        // 小V图片: 第二轮公布后及已完成用per2，其他用per1
        const isFinished = this.isFinishedStatus(progressStatus);
        const vImageUrl = `${BASE_IMG_URL}/product/${isFinished ? 'per2.png' : 'per1.png'}`;
        
        return (
            <>
                <div className={classNames(styles['speed-view'])}>
                    <div className={classNames(styles.progressTitleBox)}>
                        <div className={classNames(styles.title)}>
                            {statusText}
                        </div>
                        <div className={styles.userBox}>
                            {data?.latest_enroll_users?.length > 0 && <AvatarList userList={data?.latest_enroll_users}></AvatarList>}
                            <div className={styles.userNum}>{data?.enrolls || 0}人已报名</div>
                        </div>
                    </div>
                    <div className={classNames(styles.progress)}>
                        <img 
                            src={vImageUrl} 
                            alt="" 
                            className={styles.progressSpeed} 
                            style={{ left: `${thumbPosition}%`, marginLeft: this.getMarginLeft(thumbPosition) }}
                        />
                        {/* 进度填充条 */}
                        <div className={styles.progressGap} style={{ width: `${progressWidth}%` }}></div>
                    </div>
                    <div className={classNames(styles.progressTime)}>
                        <div className={classNames(styles.timeItem, styles.timeItemActive)}>
                            <div className={styles.timeUnit}>报名时间</div>
                            <div className={styles.timeView}>{startTime}</div>
                        </div>
                        <div className={classNames(styles.timeItem, this.isTimeItemActive(progressStatus, 1) ? styles.timeItemActive : '')}>
                            <div className={styles.timeUnit}>第一轮名单公布</div>
                            <div className={styles.timeView}>{moveTime}</div>
                        </div>
                        <div className={classNames(styles.timeItem, this.isTimeItemActive(progressStatus, 2) ? styles.timeItemActive : '')}>
                            <div className={styles.timeUnit}>第二轮名单公布</div>
                            <div className={styles.timeView}>{endTime}</div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default withRouter(ProductProgressBar);